import {
  Camera,
  User,
} from "lucide-react";
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import BottomNavBar from "../../components/BottomNavBar";
import { cn } from "../../lib/utils";
import {
  gluceraDateCaption,
  gluceraGreeting,
  gluceraPageTitle,
  gluceraPageTitleResponsive,
  gluceraBrandSolidBlueButton,
} from "../../utils/gluceraTypography";
import BestScanningPractices from "../../components/BestScanningPractices";
import welcomeBg from "../../assets/homebg.png";
import { useNavigate, useSearchParams } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import homeService, { type HomePageData } from "../../services/HomeService";
import mealApiService from "../../services/MealApiService";
import {
  extractMealsArrayFromResponse,
  findMealById,
  getLatestGlucoseSpikeMeal,
  getLatestMealByTime,
  normalizeMealRecord,
  resolveActiveMealGlucoseSpike,
} from "../../utils/resolveActiveMealHomeContext";

import GlucoseInterventionCard from "../../components/HomePage/GlucoseInterventionCard";
import ClosedLoopHomeSection from "../../components/HomePage/ClosedLoopHomeSection";
import IdleHomeSection from "../../components/HomePage/IdleHomeSection";
import OnboardingHomeSection from "../../components/HomePage/OnboardingHomeSection";
import NoMealTodayHomeSection from "../../components/HomePage/NoMealTodayHomeSection";
import HomeFoodActionSheet from "../../components/HomePage/HomeFoodActionSheet";
import HomeMembershipApprovalModal from "../../components/HomePage/HomeMembershipApprovalModal";
import MealApprovalModal from "../../components/MealApprovalModal";
import HomeDateCalendar from "../../components/HomePage/HomeDateCalendar";
import PastDateDaySummaryCard from "../../components/HomePage/PastDateDaySummaryCard";
import HomePoweringPredictionList from "../../components/HomePage/HomePoweringPredictionList";
import HomePostMealExtrasSection from "../../components/HomePage/HomePostMealExtrasSection";
import HomeBbsPillarsSummary from "../../components/HomePage/HomeBbsPillarsSummary";
import FamilyGroupsSheet from "../../components/FamilyGroupsSheet";
import {
  deriveHomeAppState,
  hasMealsLoggedToday,
  type HomePrimaryState,
  type HomeSecondaryState,
} from "../../utils/deriveHomeAppState";
import { getActionWindowMinutesRemaining } from "../../utils/buildInterventionChartData";
import CreateGroupModal from "../../components/CreateGroupModal";
import { useGluceraAuth } from "../../hooks/useGluceraAuth";
import {
  useApproveMeal,
  useDenyMeal,
} from "../../hooks/useFamily";
import { mergeGlucoseReadingsDisplay } from "../../utils/glucoseReadingsModel";
import glucoseReadingsService from "../../services/GlucoseReadingsService";
import {
  formatHomeSelectedDate,
  getLocalDateString,
  isFutureDateString,
  validateGroupId,
  validateMealId,
} from "../../utils/homePageUtils";
import {
  useEnsureSessionReady,
  SESSION_BUFFER_MS,
} from "../../hooks/useEnsureSessionReady";
import { ensureFreshAccessToken, hasAuthSession } from "@/services/tokenRefresh";
import { logoutIfSessionInvalid } from "../../utils/authSessionInvalidation";

const HomePage = () => {
  const navigate = useNavigate();
  const { logout } = useGluceraAuth();
  const [userProfile, setUserProfile] = useState(
    JSON.parse(localStorage.getItem("userProfile") || "{}")
  );
  const [showBestPractices, setShowBestPractices] = useState(false);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [familyGroupsSheetOpen, setFamilyGroupsSheetOpen] = useState(false);
  const [showMembershipApprovalModal, setShowMembershipApprovalModal] =
    useState(false);
  const [familyMealApprovalToken, setFamilyMealApprovalToken] = useState<
    string | null
  >(null);
  const [showFamilyMealApprovalModal, setShowFamilyMealApprovalModal] =
    useState(false);
  const [shownFamilyMealTokens, setShownFamilyMealTokens] = useState<
    Set<string>
  >(new Set());
  const [showFoodActionSheet, setShowFoodActionSheet] = useState(false);
  const [homeContentView, setHomeContentView] = useState<
    "dashboard" | "no_meal" | "active" | "onboarding"
  >("dashboard");
  const [selectedPrimary, setSelectedPrimary] = useState<HomePrimaryState | null>(
    null
  );
  const [selectedSecondary, setSelectedSecondary] =
    useState<HomeSecondaryState | null>(null);
  const [startedIntervention, setStartedIntervention] = useState<{
    mealId: string;
    activityKey: string;
    activityLabel: string;
  } | null>(() => {
    try {
      const saved = sessionStorage.getItem("glucera_started_intervention");
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  // Persist startedIntervention so it survives page navigation
  useEffect(() => {
    try {
      if (startedIntervention) {
        sessionStorage.setItem("glucera_started_intervention", JSON.stringify(startedIntervention));
      } else {
        sessionStorage.removeItem("glucera_started_intervention");
      }
    } catch { /* ignore */ }
  }, [startedIntervention]);

  // Mutations for group membership approve / reject
  const approveMealMutation = useApproveMeal();
  const denyMealMutation = useDenyMeal();
  const [isApprovingTokens, setIsApprovingTokens] = useState(false);
  const [isRejectingMealTokens, setIsRejectingMealTokens] = useState(false);

  // Read current user id from localStorage userInfo
  const currentUserId = useMemo(() => {
    try {
      const raw = localStorage.getItem("userInfo");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.id || null;
    } catch {
      return null;
    }
  }, []);

  // Glucose Readings from localStorage (same as profile page)
  const [glucoseReadings, setGlucoseReadings] = useState(() => {
    try {
      const stored = localStorage.getItem("glucose_readings");
      return stored
        ? JSON.parse(stored)
        : { cgm: "", fingerPrick: "", fasting: "", hba1c: "" };
    } catch {
      return { cgm: "", fingerPrick: "", fasting: "", hba1c: "" };
    }
  });

  const [activeGroupId] = useState<string | undefined>(
    validateGroupId(localStorage.getItem("family_group_id"))
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedDate, setSelectedDate] = useState<string>(
    searchParams.get("date") || getLocalDateString()
  );
  const [isDateChanging, setIsDateChanging] = useState(false);

  const formattedSelectedDate = formatHomeSelectedDate(selectedDate);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { canUseAuthenticatedApis } = useEnsureSessionReady();

  useEffect(() => {
    const current = searchParams.get("date");
    if (selectedDate && selectedDate !== current) {
      setSearchParams({ date: selectedDate });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const {
    data: homeData,
    isLoading: loading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["homePageData", selectedDate, activeGroupId],
    queryFn: async () => {
      await ensureFreshAccessToken(SESSION_BUFFER_MS);
      try {
        const data = await homeService.getHomePageData(
          selectedDate,
          activeGroupId,
          { showNotification: false, notifyMessage: false }
        );
        return data;
      } catch (error) {
        logoutIfSessionInvalid(error);
        throw error;
      }
    },
    enabled: canUseAuthenticatedApis,
    staleTime: 3 * 60 * 1000, // 3 min cache — back navigation par re-fetch nahi hoga
    gcTime: 10 * 60 * 1000,
    refetchOnMount: true, // sirf tab fetch karo jab data stale ho
    refetchOnWindowFocus: false,
  });

  // Pull-to-refresh — defined AFTER refetch is available
  const [isPullRefreshing, setIsPullRefreshing] = useState(false);
  const touchStartY = useRef<number>(0);
  const pullDist = useRef<number>(0);
  const [pullY, setPullY] = useState(0);
  const PULL_THRESHOLD = 70;

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if ((scrollContainerRef.current?.scrollTop ?? 1) === 0) {
      touchStartY.current = e.touches[0].clientY;
    }
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStartY.current) return;
    const diff = e.touches[0].clientY - touchStartY.current;
    if (diff > 0 && (scrollContainerRef.current?.scrollTop ?? 1) === 0) {
      pullDist.current = diff;
      setPullY(Math.min(diff * 0.35, 55));
    }
  }, []);

  const onTouchEnd = useCallback(async () => {
    if (pullDist.current > PULL_THRESHOLD) {
      setIsPullRefreshing(true);
      setIsDateChanging(true);
      await refetch();
      setIsPullRefreshing(false);
    }
    touchStartY.current = 0;
    pullDist.current = 0;
    setPullY(0);
  }, [refetch]);

  const { data: apiGlucoseReadings, refetch: refetchGlucoseReadings } =
    useQuery({
      queryKey: ["glucoseReadings"],
      queryFn: () => glucoseReadingsService.getReadings(),
      enabled: canUseAuthenticatedApis,
      staleTime: 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    });

  const glucoseReadingsForCard = useMemo(
    () =>
      mergeGlucoseReadingsDisplay(apiGlucoseReadings ?? null, glucoseReadings),
    [apiGlucoseReadings, glucoseReadings]
  );

  // Detect pending membership approval: find a member whose user_id matches
  // the current user AND has is_approve === true in any group of Family Health
  const pendingMembershipApproval = useMemo(() => {
    if (!currentUserId || !homeData?.["Family Health"]?.groups) return null;
    for (const group of homeData["Family Health"].groups) {
      const match = (group.members || []).find(
        m => m.user_id === currentUserId && m.is_approve === true
      );
      if (match) {
        return {
          groupId: group.id,
          groupName: group.name || "Health Group",
          memberDisplayName: match.display_name,
          memberInitial:
            match.initial || match.display_name.charAt(0).toUpperCase(),
          approvalTokens: match.approval_tokens || [],
        };
      }
    }
    return null;
  }, [homeData, currentUserId]);

  // Show approval modal whenever a pending approval is detected
  useEffect(() => {
    if (pendingMembershipApproval) {
      setShowMembershipApprovalModal(true);
    }
  }, [pendingMembershipApproval]);

  // Family meal-log approvals from home data (not current user's own membership flow)
  useEffect(() => {
    if (
      !homeData ||
      showFamilyMealApprovalModal ||
      showMembershipApprovalModal
    ) {
      return;
    }

    const approvalTokens: string[] = [];
    const groups = homeData?.["Family Health"]?.groups;
    if (Array.isArray(groups)) {
      for (const group of groups) {
        for (const member of group.members || []) {
          if (member.is_current_user && member.is_approve) continue;
          if (Array.isArray(member.approval_tokens)) {
            for (const token of member.approval_tokens) {
              if (token) approvalTokens.push(token);
            }
          }
        }
      }
    }

    if (!approvalTokens.length) return;

    const nextPending = approvalTokens.find(
      token => !shownFamilyMealTokens.has(token)
    );
    if (nextPending) {
      setFamilyMealApprovalToken(nextPending);
      setShowFamilyMealApprovalModal(true);
      setShownFamilyMealTokens(prev => new Set(prev).add(nextPending));
    }
  }, [
    homeData,
    showFamilyMealApprovalModal,
    showMembershipApprovalModal,
    shownFamilyMealTokens,
  ]);

  const handleFamilyMealApprovalClose = () => {
    setShowFamilyMealApprovalModal(false);
    setFamilyMealApprovalToken(null);

    setTimeout(() => {
      const approvalTokens: string[] = [];
      const groups = homeData?.["Family Health"]?.groups;
      if (Array.isArray(groups)) {
        for (const group of groups) {
          for (const member of group.members || []) {
            if (member.is_current_user && member.is_approve) continue;
            if (Array.isArray(member.approval_tokens)) {
              for (const token of member.approval_tokens) {
                if (token) approvalTokens.push(token);
              }
            }
          }
        }
      }
      const nextPending = approvalTokens.find(
        token => !shownFamilyMealTokens.has(token)
      );
      if (nextPending) {
        setFamilyMealApprovalToken(nextPending);
        setShowFamilyMealApprovalModal(true);
        setShownFamilyMealTokens(prev => new Set(prev).add(nextPending));
      }
    }, 400);
  };

  // Check if user data is available after homeData loads
  // Only logout if there's genuinely no auth token — homeData.user can be empty for past dates
  useEffect(() => {
    if (!loading && homeData) {
      const userName = homeData?.user || userProfile.name;

      if (!userName || userName.trim() === "") {
        const token = localStorage.getItem("glucera_access_token");
        // Only log out when there is truly no session left. A missing access
        // token alone is not enough — if a refresh token still exists, the
        // refresh flow will restore the access token. Logging out here on a
        // transient/expired access token was kicking active users to login.
        if (!token && !hasAuthSession()) {
          console.warn("No user data and no session found - logging out");
          logout();
        }
      }
    }
  }, [homeData, loading, userProfile.name, logout]);

  // Reset isDateChanging once fetch completes
  useEffect(() => {
    if (!isFetching) setIsDateChanging(false);
  }, [isFetching]);

  // Sync fasting timer with API + localStorage (IdleHomeSection reads from storage/API)
  useEffect(() => {
    const savedFastingState = localStorage.getItem("fastingState");
    const savedStartTime = localStorage.getItem("fastingStartTime");

    if (!homeData?.["Intermittent Fasting"]) return;

      const fastingData = homeData["Intermittent Fasting"];
      const isApiFasting =
        fastingData.Status === "Fasting" &&
        fastingData["Elapsed Time UI"] &&
        fastingData["Elapsed Seconds"] !== null;

      if (
        isApiFasting &&
        fastingData["Time Started"] &&
        fastingData["Elapsed Seconds"] !== null
      ) {
        const now = Date.now();
        const elapsedMilliseconds = fastingData["Elapsed Seconds"] * 1000;
        const startTime = now - elapsedMilliseconds;
        const lastStoppedTime = localStorage.getItem("lastStoppedTime");
        if (lastStoppedTime && startTime < parseInt(lastStoppedTime)) {
          return;
        }

        if (
          !savedStartTime ||
          Math.abs(parseInt(savedStartTime) - startTime) > 5000
        ) {
          localStorage.setItem("fastingState", "true");
          localStorage.setItem("fastingStartTime", startTime.toString());
          if (lastStoppedTime) {
            localStorage.removeItem("lastStoppedTime");
          }
        }
      } else if (!isApiFasting && savedFastingState !== "true") {
        localStorage.setItem("fastingState", "false");
        localStorage.removeItem("fastingStartTime");
    }
  }, [homeData]);

  // Refresh all data — always uses latest selectedDate via useCallback deps
  const refreshAllData = useCallback(() => {
    refetch();
    refetchGlucoseReadings();
    setUserProfile(JSON.parse(localStorage.getItem("userProfile") || "{}"));
    try {
      const stored = localStorage.getItem("glucose_readings");
      if (stored) setGlucoseReadings(JSON.parse(stored));
    } catch {
      /* ignore */
    }
  }, [refetch, refetchGlucoseReadings]);

  // Auto-Refresh Logic — re-registers listeners when refreshAllData changes (i.e., date changes)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshAllData();
      }
    };

    const handleFocus = () => {
      refreshAllData();
    };

    const handleMedicationUpdate = () => {
      refreshAllData();
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (
        e.key === "userProfile" ||
        e.key === "user_preferences" ||
        e.key === "glucera_weight_unit" ||
        e.key === "glucera_height_unit"
      ) {
        refetch();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("medicationsUpdated", handleMedicationUpdate);
    window.addEventListener("storage", handleStorageChange);

    const interval = setInterval(
      () => {
        refreshAllData();
      },
      5 * 60 * 1000
    );

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("medicationsUpdated", handleMedicationUpdate);
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, [refreshAllData, refetch]);

  const handleDateSelect = async (dateString: string) => {
    if (isFutureDateString(dateString)) {
      return;
    }

    setIsDateChanging(true);
    setSelectedDate(dateString);
    setIsCalendarOpen(false);
  };

  const homeAppState = useMemo(
    () =>
      deriveHomeAppState(homeData, {
        selectedDate,
        todayDate: getLocalDateString(),
        hasCgmReading: Boolean(
          glucoseReadingsForCard.cgm &&
            String(glucoseReadingsForCard.cgm).trim() !== "" &&
            String(glucoseReadingsForCard.cgm).trim() !== "—"
        ),
        isNewUser: homeData?.["User Status"]?.is_new_user,
      }),
    [homeData, selectedDate, glucoseReadingsForCard.cgm]
  );

  const isSelectedDateToday = selectedDate === getLocalDateString();

  const showNoMealTodayOption = useMemo(
    () => !hasMealsLoggedToday(homeData),
    [homeData]
  );

  const showActiveTab = useMemo(
    () => isSelectedDateToday && hasMealsLoggedToday(homeData),
    [isSelectedDateToday, homeData]
  );

  const showFourPillars = useMemo(
    () =>
      hasMealsLoggedToday(homeData) &&
      homeContentView !== "no_meal" &&
      homeContentView !== "onboarding",
    [homeData, homeContentView],
  );

  const activeMealContext = useMemo(() => {
    const latestMealFromApi = getLatestGlucoseSpikeMeal(homeData);
    const mealId = validateMealId(latestMealFromApi?.meal_id ?? null);
    const hasLiveMeal =
      Boolean(mealId) &&
      Boolean(homeData?.glucose_spike || latestMealFromApi);
    return { latestMealFromApi, mealId, hasLiveMeal };
  }, [homeData]);

  const { data: todayMealsResponse } = useQuery({
    queryKey: ["home-active-meal-detail", selectedDate],
    queryFn: () => mealApiService.getMealDataByDate(selectedDate),
    enabled:
      canUseAuthenticatedApis &&
      isSelectedDateToday &&
      hasMealsLoggedToday(homeData),
    staleTime: 60_000,
  });

  const todayMealsNormalized = useMemo(() => {
    if (!todayMealsResponse) return [];
    return extractMealsArrayFromResponse(todayMealsResponse).map((meal) =>
      normalizeMealRecord(meal as Record<string, unknown>)
    );
  }, [todayMealsResponse]);

  const latestMealFromMealData = useMemo(
    () => getLatestMealByTime(todayMealsNormalized),
    [todayMealsNormalized]
  );

  const resolvedActiveMealId = useMemo(
    () =>
      validateMealId(
        activeMealContext.mealId ??
          latestMealFromMealData?.meal_id ??
          null
      ),
    [activeMealContext.mealId, latestMealFromMealData?.meal_id]
  );

  const activeMealDetail = useMemo(() => {
    if (!resolvedActiveMealId) return latestMealFromMealData;
    return (
      findMealById(todayMealsNormalized, resolvedActiveMealId) ??
      latestMealFromMealData
    );
  }, [todayMealsNormalized, resolvedActiveMealId, latestMealFromMealData]);

  const activeMealGlucoseSpike = useMemo(
    () =>
      resolveActiveMealGlucoseSpike(
        homeData,
        activeMealContext.latestMealFromApi,
        activeMealDetail
      ),
    [homeData, activeMealContext.latestMealFromApi, activeMealDetail]
  );

  const latestMealForCard = useMemo(
    () => activeMealDetail ?? activeMealContext.latestMealFromApi,
    [activeMealDetail, activeMealContext.latestMealFromApi]
  );

  const hasActiveLiveMeal = useMemo(
    () =>
      Boolean(resolvedActiveMealId) &&
      Boolean(
        homeData?.glucose_spike ||
          activeMealContext.latestMealFromApi ||
          activeMealDetail
      ),
    [
      resolvedActiveMealId,
      homeData?.glucose_spike,
      activeMealContext.latestMealFromApi,
      activeMealDetail,
    ]
  );

  useEffect(() => {
    setStartedIntervention((prev) => {
      if (prev && prev.mealId !== resolvedActiveMealId) {
        // New meal — clear sessionStorage too
        try { sessionStorage.removeItem("glucera_started_intervention"); } catch { /* ignore */ }
        return null;
      }
      return prev;
    });
    // New meal — leave idle dashboard so live / closed-loop flow can run again
    setSelectedSecondary((prev) => (prev === "idle" ? null : prev));
  }, [resolvedActiveMealId]);

  const userStartedIntervention =
    startedIntervention?.mealId === resolvedActiveMealId;

  const {
    data: actionWindowIntervention,
    isSuccess: isActionWindowLoaded,
  } = useQuery({
    queryKey: ["meal-action-window", resolvedActiveMealId],
    queryFn: () =>
      homeService.calculateIntervention(resolvedActiveMealId!, "walking"),
    enabled:
      canUseAuthenticatedApis &&
      Boolean(resolvedActiveMealId) &&
      Boolean(homeData) &&
      showActiveTab &&
      isSelectedDateToday,
    staleTime: 60_000,
    refetchInterval: (query) => {
      const remaining = query.state.data?.best_window_minutes_remaining;
      if (remaining == null) return 30_000;
      return remaining > 0 ? 30_000 : false;
    },
  });

  const actionWindowMinutesRemaining = isActionWindowLoaded
    ? getActionWindowMinutesRemaining(
        actionWindowIntervention,
        Number(activeMealGlucoseSpike?.time_to_peak_min) ||
          homeData?.glucose_spike?.time_to_peak_min ||
          30
      )
    : null;

  const actionWindowOpen =
    showActiveTab &&
    hasActiveLiveMeal &&
    (!isActionWindowLoaded || (actionWindowMinutesRemaining ?? 0) > 0);

  /** Live meal window open — Active tab content + highlight */
  const showActiveStatePill =
    showActiveTab && hasActiveLiveMeal && actionWindowOpen;

  const showClosedLoopStatePill =
    showActiveTab &&
    hasActiveLiveMeal &&
    isActionWindowLoaded &&
    !actionWindowOpen;

  /** Idle tab only when Closed loop tab is visible (window closed after meal) */
  const showIdleStatePill = showClosedLoopStatePill;

  // intervention_started:true = user has completed the activity (field must be explicitly present)
  const apiInterventionStarted =
    actionWindowIntervention?.intervention_started !== undefined &&
    actionWindowIntervention.intervention_started === true;

  const showClosedLoopContent =
    showClosedLoopStatePill || userStartedIntervention || apiInterventionStarted;

  const showActiveLiveContent =
    showActiveStatePill && !userStartedIntervention && !apiInterventionStarted;

  const displayPrimary: HomePrimaryState = useMemo(() => {
    if (showNoMealTodayOption) {
      return selectedPrimary ?? "no_meal_today";
    }
    if (showActiveStatePill) {
      return selectedPrimary ?? "active";
    }
    if (showClosedLoopStatePill) {
      if (selectedPrimary === "onboarding") return "active";
      return selectedPrimary ?? "active";
    }
    if (selectedPrimary) return selectedPrimary;
    if (showActiveTab) return "active";
    return homeAppState.primary;
  }, [
    selectedPrimary,
    showNoMealTodayOption,
    showActiveStatePill,
    showClosedLoopStatePill,
    showActiveTab,
    homeAppState.primary,
  ]);

  const displaySecondary: HomeSecondaryState = useMemo(() => {
    if (homeContentView === "dashboard" && showIdleStatePill) return "idle";
    if (showClosedLoopStatePill) return "closed_loop";
    if (selectedSecondary === "closed_loop" || selectedSecondary === "idle") {
      return selectedSecondary;
    }
    return selectedSecondary ?? homeAppState.secondary;
  }, [
    homeContentView,
    showIdleStatePill,
    showClosedLoopStatePill,
    selectedSecondary,
    homeAppState.secondary,
  ]);

  useEffect(() => {
    if (!showActiveTab || !hasActiveLiveMeal) {
      return;
    }

    // User chose Back to home / Idle — keep dashboard (Last meal, Health Insights, etc.)
    if (selectedSecondary === "idle" && homeContentView === "dashboard") {
      return;
    }

    // Live action window — always surface active content (Meal is live card)
    if (showActiveLiveContent) {
      setHomeContentView("active");
      setSelectedPrimary("active");
      setSelectedSecondary(null);
      return;
    }

    // Waiting for action-window API after meal log — stay on active, not idle dashboard
    if (!isActionWindowLoaded) {
      setHomeContentView("active");
      setSelectedPrimary("active");
      return;
    }

    if (!actionWindowOpen) {
      // Action window closed (post-meal). The Closed Loop toggle/"You bent it"
      // screen was removed for being confusing — land directly on the Idle
      // dashboard (Last meal + Health Insights) instead.
      setSelectedSecondary("idle");
      setSelectedPrimary(null);
      setHomeContentView("dashboard");
    } else {
      setSelectedPrimary("active");
      setSelectedSecondary(null);
      setHomeContentView("active");
    }
  }, [
    showActiveTab,
    hasActiveLiveMeal,
    actionWindowOpen,
    isActionWindowLoaded,
    showActiveLiveContent,
    selectedSecondary,
    homeContentView,
  ]);

  useEffect(() => {
    // Only applies to today — past dates use "dashboard" for nutrition summary
    if (!isSelectedDateToday) return;
    if (homeContentView !== "dashboard" || showIdleStatePill) return;
    if (selectedSecondary === "idle") return;
    setHomeContentView("active");
    setSelectedSecondary(showClosedLoopStatePill ? "closed_loop" : null);
  }, [
    isSelectedDateToday,
    homeContentView,
    showIdleStatePill,
    showClosedLoopStatePill,
    selectedSecondary,
  ]);

  useEffect(() => {
    if (!showClosedLoopStatePill || !showIdleStatePill) return;
    if (selectedPrimary === "onboarding" || homeContentView === "onboarding") {
      setSelectedPrimary(null);
      setSelectedSecondary("closed_loop");
      setHomeContentView("active");
    }
  }, [
    showClosedLoopStatePill,
    showIdleStatePill,
    selectedPrimary,
    homeContentView,
  ]);

  useEffect(() => {
    if (!isSelectedDateToday) {
      // Past date: same condition — no meals → no_meal view, meals → dashboard
      if (!hasMealsLoggedToday(homeData)) {
        setHomeContentView("no_meal");
      } else {
        setHomeContentView("dashboard");
      }
      setSelectedPrimary(null);
      return;
    }
    // API-driven new user → always show onboarding
    if (homeData?.["User Status"]?.is_new_user === true) {
      setHomeContentView("onboarding");
      setSelectedPrimary("onboarding");
      return;
    }
    if (showNoMealTodayOption) {
      setHomeContentView("no_meal");
      setSelectedPrimary("no_meal_today");
    } else if (showActiveTab) {
      // Post-meal mode: action-window effect handles view transitions — don't interfere
      if (showClosedLoopStatePill) return;
      setHomeContentView("active");
      setSelectedPrimary("active");
    } else {
      setHomeContentView("dashboard");
      setSelectedPrimary(null);
      setSelectedSecondary("idle");
    }
  }, [isSelectedDateToday, showNoMealTodayOption, showActiveTab, homeData, showClosedLoopStatePill]);

  const handlePrimaryStateSelect = useCallback((state: HomePrimaryState) => {
    setSelectedPrimary(state);
    if (state === "no_meal_today") {
      setHomeContentView("no_meal");
    } else if (state === "active") {
      setHomeContentView("active");
      setSelectedSecondary(null);
    } else if (state === "onboarding") {
      setHomeContentView("onboarding");
    } else {
      setHomeContentView("onboarding");
    }
  }, []);

  const handleSecondaryStateSelect = useCallback((state: HomeSecondaryState) => {
    setSelectedSecondary(state);
    if (state === "idle") {
      setStartedIntervention(null);
      setHomeContentView("dashboard");
      return;
    }
    if (state === "closed_loop") {
      setHomeContentView("active");
      return;
    }
    if (showActiveTab) {
      setHomeContentView("active");
      setSelectedPrimary("active");
    }
  }, [showActiveTab]);

  const handleLogFirstMeal = useCallback(() => {
    window.dispatchEvent(new CustomEvent("glucera-open-meal-camera"));
  }, []);

  const queryClient = useQueryClient();

  const handleInterventionStart = useCallback(
    async (activity: { activityKey: string; label: string }) => {
      if (!resolvedActiveMealId) return;
      // Send intervention_started:true to API so backend marks it
      try {
        await homeService.calculateIntervention(resolvedActiveMealId, activity.activityKey, true);
        // Invalidate so fresh data (intervention_started:true) is fetched
        await queryClient.invalidateQueries({ queryKey: ["meal-action-window", resolvedActiveMealId] });
      } catch { /* silent */ }
      setStartedIntervention({
        mealId: resolvedActiveMealId,
        activityKey: activity.activityKey,
        activityLabel: activity.label,
      });
      setHomeContentView("active");
      setSelectedSecondary("closed_loop");
      setSelectedPrimary(null);
    },
    [resolvedActiveMealId, queryClient]
  );

  const handleClosedLoopContinue = useCallback(() => {
    setStartedIntervention(null);
    sessionStorage.removeItem("glucera_started_intervention");
    setSelectedSecondary("idle");
    setSelectedPrimary(null);
    setHomeContentView("dashboard");
  }, []);

  const handleMembershipReject = useCallback(async () => {
    if (!pendingMembershipApproval) return;
    try {
      const tokens = pendingMembershipApproval.approvalTokens;
      if (tokens.length > 0) {
        setIsRejectingMealTokens(true);
        for (const token of tokens) {
          await denyMealMutation.mutateAsync({ token });
        }
      }
      setShowMembershipApprovalModal(false);
    } catch {
      // error handled in hook
    } finally {
      setIsRejectingMealTokens(false);
    }
  }, [pendingMembershipApproval, denyMealMutation]);

  const handleMembershipApprove = useCallback(async () => {
    if (!pendingMembershipApproval) return;
    try {
      const tokens = pendingMembershipApproval.approvalTokens;
      if (tokens.length > 0) {
        setIsApprovingTokens(true);
        for (const token of tokens) {
          await approveMealMutation.mutateAsync(token);
        }
      }
      setShowMembershipApprovalModal(false);
    } catch {
      // error handled in hook
    } finally {
      setIsApprovingTokens(false);
    }
  }, [pendingMembershipApproval, approveMealMutation]);


  // Show loading state while fetching data
  if (loading) {
    return (
      <div
        className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
        style={{
          minHeight: "100vh",
          maxHeight: "100vh",
          overflow: "hidden",
        }}
      >
        <div className="flex h-screen items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <p className="text-sm text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <HomeFoodActionSheet
        open={showFoodActionSheet}
        onClose={() => setShowFoodActionSheet(false)}
      />

      {/* Best Scanning Practices Modal */}
      {showBestPractices && (
        <BestScanningPractices
          onClose={() => setShowBestPractices(false)}
          onScanNow={() => setShowBestPractices(false)}
        />
      )}

      {/* Create Group Modal */}
      <CreateGroupModal
        isOpen={showCreateGroupModal}
        onClose={() => {
          setShowCreateGroupModal(false);
          void refetch();
          queryClient.invalidateQueries({ queryKey: ["family", "groups"] });
        }}
      />

      <FamilyGroupsSheet
        open={familyGroupsSheetOpen}
        onClose={() => setFamilyGroupsSheetOpen(false)}
        onCreateGroup={() => setShowCreateGroupModal(true)}
      />

      {pendingMembershipApproval && (
        <HomeMembershipApprovalModal
          approval={pendingMembershipApproval}
          open={showMembershipApprovalModal}
          onOpenChange={(open) => {
            if (!open) setShowMembershipApprovalModal(false);
          }}
          isApproving={isApprovingTokens}
          isRejecting={isRejectingMealTokens}
          onApprove={handleMembershipApprove}
          onReject={handleMembershipReject}
        />
      )}

      <MealApprovalModal
        approvalToken={familyMealApprovalToken}
        isOpen={showFamilyMealApprovalModal}
        onClose={handleFamilyMealApprovalClose}
      />

      <div
        className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
        style={{
          minHeight: "100vh",
          maxHeight: "100vh",
          overflow: "hidden",
        }}
      >
        {/* Blurred Background Image - Only within container */}
        <div
          className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
          style={{
            backgroundImage: `url(${welcomeBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />

        {/* Overlay for better contrast - Only within container */}
        <div className="absolute inset-0 bg-background/80" />

        {/* Fetching indicator — only shows when user actively switches dates */}
        {isFetching && !loading && isDateChanging && !isPullRefreshing && (
          <div className="absolute left-0 right-0 top-0 z-20 h-1 overflow-hidden bg-transparent">
            <div className="h-full animate-[shimmer_1.2s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-primary to-transparent" />
          </div>
        )}

        {/* Pull-to-refresh indicator */}
        {(pullY > 0 || isPullRefreshing) && (
          <div
            className="absolute left-0 right-0 z-30 flex items-center justify-center transition-all duration-150"
            style={{ top: isPullRefreshing ? 10 : pullY - 40 }}
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card shadow-md">
              <svg
                className={`h-5 w-5 text-primary ${isPullRefreshing ? "animate-spin" : ""}`}
                style={{
                  transform: isPullRefreshing
                    ? undefined
                    : `rotate(${Math.min((pullY / 55) * 360, 360)}deg)`,
                }}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
            </div>
          </div>
        )}

        <div
          ref={scrollContainerRef}
          className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto px-4 pb-6"
          style={{ height: "calc(100vh - 80px)" }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Header — same on Idle and Closed loop tabs */}
          <div className="flex items-start justify-between gap-3 pb-2 pt-0.5">
            <div className="min-w-0 flex-1">
              {selectedDate === getLocalDateString() ? (
                <>
                  <p className={gluceraGreeting}>
                    {homeData?.greeting?.trim() || "Welcome,"}
                  </p>
                  <h1 className={cn("mt-1 break-words", gluceraPageTitle)}>
                    {homeData?.user || userProfile.name || ""}
                  </h1>
                  <p className={cn("mt-1", gluceraDateCaption)}>
                    {formattedSelectedDate}
                  </p>
                </>
              ) : (
                <>
                  <p className={gluceraGreeting}>
                    Health summary
                  </p>
                  <h1 className={cn("mt-1", gluceraPageTitleResponsive)}>
                    {formattedSelectedDate}
                  </h1>
                </>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2 pt-0.5">
              {/* + Add Food Button — temporarily hidden
              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowFoodActionSheet(true)}
                className="h-10 w-10 rounded-xl border-border bg-card/80 hover:bg-muted"
                aria-label="Add food"
              >
                <Plus className="h-5 w-5 text-blue-500" />
              </Button>
              */}
              <HomeDateCalendar
                open={isCalendarOpen}
                onOpenChange={setIsCalendarOpen}
                currentMonth={currentMonth}
                onCurrentMonthChange={setCurrentMonth}
                selectedDate={selectedDate}
                onDateSelect={handleDateSelect}
              />
              {/* Profile Button — rightmost corner */}
              <button
                type="button"
                onClick={() => navigate("/glucera/profilepage")}
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-card hover:bg-muted"
                aria-label="Profile"
              >
                <User className="h-5 w-5 text-[#737DFE]" />
              </button>
            </div>
            {/* end flex items-center gap-2 buttons wrapper */}
          </div>

          {/* Closed Loop ↔ Idle toggle removed — it confused users. Post-meal
              now shows the Idle dashboard directly (see action-window effect). */}

          {homeContentView === "no_meal" ? (
            <NoMealTodayHomeSection
              onLogMeal={handleLogFirstMeal}
              onRecalibrate={() =>
                navigate(`/glucera${GluceraNavigationRoutes.GLUCOSE_READINGS}`)
              }
              homeData={homeData}
              isToday={isSelectedDateToday}
            />
          ) : homeContentView === "dashboard" ? (
            <div className="space-y-2">
          {isSelectedDateToday ? (
            <IdleHomeSection
              homeData={homeData}
              userName={homeData?.user || userProfile.name || ""}
              latestMeal={latestMealForCard}
              mealId={resolvedActiveMealId}
              glucoseSpikeData={activeMealGlucoseSpike as HomePageData["glucose_spike"]}
              selectedDate={selectedDate}
              showScorePillars={showFourPillars}
            />
          ) : (
            <>
            <PastDateDaySummaryCard nutrition={homeData?.["Today's Nutrition"]} />
            <HomeBbsPillarsSummary
              homeData={homeData}
              selectedDate={selectedDate}
              showFourPillars={showFourPillars}
            />
            </>
          )}

          {!isSelectedDateToday && (homeData?.["Powering This Prediction"] ?? []).length > 0 && (
            <HomePoweringPredictionList
              items={homeData!["Powering This Prediction"]!}
              headerLabel="You can update"
              headerHint="Improves your next prediction"
              onRecalibrate={() =>
                navigate(`/glucera${GluceraNavigationRoutes.GLUCOSE_READINGS}`)
              }
            />
          )}

          {showActiveTab ? (
            <HomePostMealExtrasSection
              homeData={homeData}
              onOpenFamilyGroups={() => setFamilyGroupsSheetOpen(true)}
            />
          ) : null}
            </div>
          ) : homeContentView === "onboarding" ? (
            <OnboardingHomeSection onLogFirstMeal={handleLogFirstMeal} />
          ) : homeContentView === "active" && showActiveTab ? (
            <div className="space-y-2">
              {showClosedLoopContent ? (
                <ClosedLoopHomeSection
                  homeData={homeData}
                  mealId={resolvedActiveMealId}
                  latestMeal={latestMealForCard}
                  glucoseSpikeData={activeMealGlucoseSpike as HomePageData["glucose_spike"]}
                  activityKey={startedIntervention?.activityKey}
                  activityLabel={startedIntervention?.activityLabel}
                  onContinue={handleClosedLoopContinue}
                  selectedDate={selectedDate}
                  showScorePillars={showFourPillars}
                />
              ) : showActiveLiveContent ? (
                <GlucoseInterventionCard
                  glucoseSpikeData={activeMealGlucoseSpike as HomePageData["glucose_spike"]}
                  mealId={resolvedActiveMealId}
                  latestMealData={latestMealForCard}
                  homeData={homeData}
                  selectedDate={selectedDate}
                  showScorePillars={showFourPillars}
                  onStartIntervention={handleInterventionStart}
                />
              ) : (
                <>
                  <Card className="rounded-2xl border border-border/70 bg-card p-5 shadow-sm">
                    <p className="text-base font-semibold text-foreground">
                      No live meal yet
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      Log a meal to see your live glucose curve, predicted peak,
                      and activity tips to bend the spike.
                    </p>
                    <Button
                      type="button"
                      onClick={handleLogFirstMeal}
                      className={cn("mt-4 h-11 w-full rounded-xl", gluceraBrandSolidBlueButton)}
                    >
                      <Camera className="mr-2 h-4 w-4" />
                      Log a meal
                    </Button>
                  </Card>
                  <HomeBbsPillarsSummary
                    homeData={homeData}
                    selectedDate={selectedDate}
                    showFourPillars={showFourPillars}
                  />
                </>
              )}
              <HomePostMealExtrasSection
                homeData={homeData}
                onOpenFamilyGroups={() => setFamilyGroupsSheetOpen(true)}
              />
            </div>
          ) : null}
        </div>

        <BottomNavBar />
      </div>
    </>
  );
};

export default HomePage;
